import { isNullish } from '@/utils/helper'
import { Button, Flex, Menu, MenuButton, MenuDivider, MenuGroup, MenuList, Portal } from '@chakra-ui/react'
import React, { useState } from 'react'
import { RxCross1 } from 'react-icons/rx'
import DateFilter from './DateFilter'
import MultipleSelectFilter from './MultipleSelectFilter'
import RangeFilter from './RangeFilter'
import SelectFilter from './SelectFilter'
import SimpleTextFilter from './SimpleTextFilter'
import TextFilter from './TextFilter'

export const FilterType = {
  SELECT: 'select',
  SELECT_YES_NO: 'selectYesNo',
  MULTI_SELECT: 'multiSelect',
  DATE: 'date',
  RANGE: 'range',
  TEXT: 'text',
  SIMPLE_TEXT: 'simpleText',
}

export const hasFilterValue = (value) =>
  !isNullish(value) && value !== '' && !(Array.isArray(value) && value.length === 0)

const TableFilter = ({ column, updatePagination }) => {
  // Component that show Difference filter base on the type
  const [isOpen, setIsOpen] = useState(false)
  // A draft while the menu is open; only Save applies it. The button reflects the
  // applied filter, never the draft, so a menu closed without saving — or a
  // filter cleared from the toolbar — cannot leave it showing "(Selected)".
  const [filterValue, setFilterValue] = useState(column.getFilterValue())
  const appliedValue = column.getFilterValue()
  const isFiltered = hasFilterValue(appliedValue)

  const { filterVariant, filterOptions } = column.columnDef.meta ?? {}

  const handleToggleMenu = () => {
    if (!isOpen) {
      setFilterValue(appliedValue)
    }
    setIsOpen((prev) => !prev)
  }

  const handleMenuClose = () => {
    setIsOpen(false)
  }

  const handleSetActualFilter = () => {
    setIsOpen(false)
    updatePagination(0)
    column.setFilterValue(hasFilterValue(filterValue) ? filterValue : undefined)
  }

  const handleClearFilter = () => {
    setFilterValue(undefined)
  }

  const directUpdateFilterValue = (value) => {
    column.setFilterValue(value)
  }

  const renderFilter = () => {
    switch (filterVariant) {
      case FilterType.SELECT:
        if (!Array.isArray(filterOptions)) {
          return <TextFilter value={filterValue} setValue={setFilterValue} />
        }

        return <SelectFilter value={filterValue} setValue={setFilterValue} options={filterOptions} />
      case FilterType.SELECT_YES_NO:
        return (
          <SelectFilter
            value={filterValue}
            setValue={setFilterValue}
            options={[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
          />
        )
      case FilterType.MULTI_SELECT:
        if (!Array.isArray(filterOptions)) {
          return <TextFilter value={filterValue} setValue={setFilterValue} />
        }
        return <MultipleSelectFilter value={filterValue} setValue={setFilterValue} options={filterOptions} />
      case FilterType.DATE:
        return <DateFilter value={filterValue} setValue={setFilterValue} />
      case FilterType.RANGE:
        return <RangeFilter value={filterValue} setValue={setFilterValue} />
      case FilterType.SIMPLE_TEXT:
        return <SimpleTextFilter value={filterValue} setValue={setFilterValue} />
      default:
        return <TextFilter value={filterValue} setValue={setFilterValue} />
    }
  }

  if (filterVariant === FilterType.SIMPLE_TEXT) {
    return <SimpleTextFilter value={appliedValue} setValue={directUpdateFilterValue} />
  }

  return (
    <Menu isOpen={isOpen} onClose={handleMenuClose}>
      <MenuButton
        w={'100%'}
        as={Button}
        bg={isFiltered ? 'teal.500' : 'gray.100'}
        color={isFiltered ? 'gray.50' : 'gray.600'}
        onClick={handleToggleMenu}
      >
        {isFiltered ? '(Selected)' : 'Filter'}
      </MenuButton>
      <Portal>
        <MenuList zIndex="999" px="2" maxW="400px">
          <MenuGroup title="Filter">{renderFilter()}</MenuGroup>

          <Button variant={'outline'} leftIcon={<RxCross1 />} onClick={handleClearFilter} mt="2" w="100%">
            Clear Filter(s)
          </Button>

          <MenuDivider />
          <Flex gap="2">
            <Button colorScheme="teal" onClick={handleSetActualFilter}>
              Save
            </Button>
            <Button colorScheme="gray" onClick={handleMenuClose}>
              Close
            </Button>
          </Flex>
        </MenuList>
      </Portal>
    </Menu>
  )
}

export default TableFilter
